import type { ReactNode } from "react";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";

export interface SegmentedTabOption<T extends string> {
  value: T;
  label: ReactNode;
}

export type SegmentedTabVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;

interface SegmentedTabsProps<T extends string> {
  options: SegmentedTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** 탭 버튼에 적용할 Button variant */
  variant?: SegmentedTabVariant;
  className?: string;
  /** 탭 버튼에 적용할 추가 className (예: 폼 필드 높이에 맞출 때) */
  buttonClassName?: string;
}

export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  variant = "default",
  className,
  buttonClassName,
}: SegmentedTabsProps<T>) {
  return (
    <div
      className={cn(
        "inline-flex items-stretch rounded-lg bg-fills-neutral inset-border-solid-normal",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Button
            key={opt.value}
            type="button"
            theme="assistive"
            variant={variant}
            size="sm"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              "whitespace-nowrap",
              active
                ? "bg-static-white inset-border-normal! text-labels-primary"
                : "bg-transparent shadow-none! text-labels-assistive hover:text-labels-secondary",
              buttonClassName,
            )}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}
